import React, { useState, useEffect, useRef } from 'react';
import { clientsService, workflowStagesService, communicationsService } from '../../services';
import { useResponsive } from '../../hooks/useResponsive';
import { useAuth } from '../../contexts/AuthContext';
import CommunicationLogger from './CommunicationLogger';
import Papa from 'papaparse';
import ClientRegistrationLink from './ClientRegistrationLink';

// Reskinned 2026-08 to the Voyager Pro design (see AGENTS.md / memory MCP
// "TPCRM MCP Server Config Fix" era session for the Figma source this was
// ported from - src/components/ClientsPage.tsx in the Quote Builder Figma
// Make file). All state/handlers/API calls are preserved from the prior
// travelieTheme-based implementation; only the render layer changed to
// Tailwind + the CSS custom properties from src/styles/voyagerTheme.css.
//
// New in this pass: the "Move Client to New Stage" modal previously had no
// UI trigger anywhere (dead code) - it's now reachable from the client
// detail panel's "Stage" quick action. Tag filtering (backed by the new
// `clients.tags` column) and a Comms history tab (communicationsService)
// are also new - everything else is a like-for-like visual port.

const PRESET_TAGS = ['VIP', 'Active', 'New', 'Prospect', 'Inactive'];
const TAG_COLORS = {
  VIP: 'bg-amber-100 text-amber-700 border border-amber-200',
  Active: 'bg-green-100 text-green-700 border border-green-200',
  New: 'bg-blue-100 text-blue-700 border border-blue-200',
  Prospect: 'bg-purple-100 text-purple-700 border border-purple-200',
  Inactive: 'bg-gray-100 text-gray-500 border border-gray-200',
};

function fmtDate(d) {
  if (!d) return '—';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split('-');
      return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
}

function parseTags(client) {
  if (Array.isArray(client.tags)) return client.tags;
  if (typeof client.tags === 'string' && client.tags) {
    try {
      const parsed = JSON.parse(client.tags);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseMembers(client) {
  if (Array.isArray(client.additional_members)) return client.additional_members;
  if (typeof client.additional_members === 'string' && client.additional_members) {
    try {
      const parsed = JSON.parse(client.additional_members);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function fullName(c) {
  return `${c.first_name || ''} ${c.last_name || ''}`.trim();
}

function initials(c) {
  return `${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}`.toUpperCase() || '?';
}

const inputCls = "w-full px-3 py-2.5 border border-[var(--border)] rounded-lg text-sm text-[var(--foreground)] bg-white placeholder:text-[var(--muted-foreground)]/60 focus:border-[var(--accent)] focus:outline-none transition-colors";
const labelCls = "block text-xs font-semibold text-[var(--muted-foreground)] mb-1.5 uppercase tracking-wide";

function primaryBtnCls(disabled) {
  return `px-5 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold transition-all ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:brightness-105 cursor-pointer'}`;
}
const secondaryBtnCls = "px-5 py-2.5 border border-[var(--border)] rounded-xl text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)] transition-colors";

// ─── Member Card ────────────────────────────────────────────────────────────

const MemberCard = ({ member, onEdit, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ ...member });

  const handleSave = () => { onEdit(editData); setIsEditing(false); };
  const handleCancel = () => { setEditData({ ...member }); setIsEditing(false); };

  return (
    <div className="bg-[var(--muted)]/30 rounded-xl border border-[var(--border)] overflow-hidden">
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="text-[var(--muted-foreground)] flex-shrink-0">
            <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <span className="text-sm font-medium text-[var(--foreground)] truncate">
            {member.first_name} {member.last_name}
            {member.email && <span className="text-[var(--muted-foreground)] font-normal"> ({member.email})</span>}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button type="button" onClick={() => setIsEditing(!isEditing)} className="text-xs font-semibold text-[var(--accent)] hover:underline">
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
          <button type="button" onClick={onRemove} className="text-xs font-semibold text-red-500 hover:underline">Remove</button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-[var(--border)] pt-3">
          {isEditing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>First Name *</label>
                  <input className={inputCls} value={editData.first_name || ''} onChange={e => setEditData({ ...editData, first_name: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Last Name *</label>
                  <input className={inputCls} value={editData.last_name || ''} onChange={e => setEditData({ ...editData, last_name: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input className={inputCls} value={editData.phone || ''} onChange={e => setEditData({ ...editData, phone: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Date of Birth</label>
                  <input type="date" className={inputCls} value={editData.date_of_birth || ''} onChange={e => setEditData({ ...editData, date_of_birth: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Passport Number</label>
                  <input className={inputCls} value={editData.passport_number || ''} onChange={e => setEditData({ ...editData, passport_number: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Passport Expiry</label>
                  <input type="date" className={inputCls} value={editData.passport_expiry || ''} onChange={e => setEditData({ ...editData, passport_expiry: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={handleCancel} className="px-3 py-1.5 text-xs font-semibold border border-[var(--border)] rounded-lg">Cancel</button>
                <button type="button" onClick={handleSave} disabled={!editData.first_name || !editData.last_name} className="px-3 py-1.5 text-xs font-semibold bg-[var(--primary)] text-white rounded-lg disabled:opacity-40">Save</button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-[var(--muted-foreground)]">
              <div>Phone: <span className="text-[var(--foreground)]">{member.phone || 'Not provided'}</span></div>
              <div>DOB: <span className="text-[var(--foreground)]">{member.date_of_birth ? fmtDate(member.date_of_birth) : 'Not provided'}</span></div>
              <div>Passport #: <span className="text-[var(--foreground)]">{member.passport_number || 'Not provided'}</span></div>
              <div>Passport Expiry: <span className="text-[var(--foreground)]">{member.passport_expiry ? fmtDate(member.passport_expiry) : 'Not provided'}</span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Add / Edit Client Form ─────────────────────────────────────────────────

const ClientFormComponent = ({ isEdit, initialData, onSubmit, onCancel, formLoading }) => {
  const formRef = useRef(null);
  const [additionalMembers, setAdditionalMembers] = useState(initialData?.additional_members || []);
  const [selectedTags, setSelectedTags] = useState(() => (initialData ? parseTags(initialData) : ['New']));
  const [newMember, setNewMember] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    date_of_birth: '', passport_number: '', passport_expiry: '', passport_country: '',
  });

  useEffect(() => {
    if (formRef.current && initialData) {
      const form = formRef.current;
      Object.keys(initialData).forEach(key => {
        if (form[key] && key !== 'additional_members') {
          if (key === 'date_of_birth' || key === 'passport_expiry') {
            const dateValue = initialData[key];
            if (dateValue) {
              const date = new Date(dateValue);
              form[key].value = !isNaN(date.getTime()) ? date.toISOString().split('T')[0] : dateValue;
            } else {
              form[key].value = '';
            }
          } else {
            form[key].value = initialData[key] || '';
          }
        }
      });
      setAdditionalMembers(parseMembers(initialData));
      setSelectedTags(parseTags(initialData));
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    data.additional_members = additionalMembers;
    data.tags = selectedTags;
    onSubmit(data);
  };

  const addMemberToGroup = () => {
    if (newMember.first_name && newMember.last_name) {
      setAdditionalMembers([...additionalMembers, { ...newMember }]);
      setNewMember({ first_name: '', last_name: '', email: '', phone: '', date_of_birth: '', passport_number: '', passport_expiry: '', passport_country: '' });
    }
  };
  const removeMemberFromGroup = (index) => setAdditionalMembers(additionalMembers.filter((_, i) => i !== index));
  const toggleTag = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
      {/* Lead Traveler */}
      <section>
        <h3 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-wide mb-4">Lead Traveler Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>First Name *</label><input type="text" name="first_name" required disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Last Name *</label><input type="text" name="last_name" required disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Email *</label><input type="email" name="email" required disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Phone *</label><input type="tel" name="phone" required disabled={formLoading} className={inputCls} /></div>
        </div>
      </section>

      {/* Tags */}
      <section>
        <h3 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-wide mb-4">Tags</h3>
        <div className="flex flex-wrap gap-2">
          {PRESET_TAGS.map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${selectedTags.includes(tag) ? TAG_COLORS[tag] : 'bg-white border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)]'}`}
            >
              {tag}
            </button>
          ))}
        </div>
      </section>

      {/* Optional Information */}
      <section>
        <h3 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-wide mb-4">Optional Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-full"><label className={labelCls}>Address</label><input type="text" name="address" disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Date of Birth</label><input type="date" name="date_of_birth" disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Passport Number</label><input type="text" name="passport_number" disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Passport Expiry</label><input type="date" name="passport_expiry" disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Passport Country</label><input type="text" name="passport_country" disabled={formLoading} className={inputCls} /></div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section>
        <h3 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-wide mb-4">Emergency Contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><label className={labelCls}>Contact Name</label><input type="text" name="emergency_contact_name" disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Contact Phone</label><input type="tel" name="emergency_contact_phone" disabled={formLoading} className={inputCls} /></div>
          <div><label className={labelCls}>Relationship</label><input type="text" name="emergency_contact_relation" disabled={formLoading} className={inputCls} /></div>
        </div>
      </section>

      {/* Loyalty Programs */}
      <section>
        <h3 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-wide mb-4">Loyalty Programs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><label className={labelCls}>Hotel Loyalty</label><input type="text" name="hotel_loyalty_number" disabled={formLoading} placeholder="e.g. Marriott Bonvoy #…" className={inputCls} /></div>
          <div><label className={labelCls}>Airline Loyalty</label><input type="text" name="airline_loyalty_number" disabled={formLoading} placeholder="e.g. Delta SkyMiles #…" className={inputCls} /></div>
          <div><label className={labelCls}>Cruise Loyalty</label><input type="text" name="cruise_loyalty_number" disabled={formLoading} placeholder="e.g. Royal Caribbean #…" className={inputCls} /></div>
        </div>
      </section>

      {/* Group Members */}
      <section>
        <h3 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-wide mb-4">Additional Group Members</h3>
        {additionalMembers.length > 0 && (
          <div className="space-y-2 mb-4">
            {additionalMembers.map((member, index) => (
              <MemberCard
                key={index}
                member={member}
                onEdit={(updated) => { const copy = [...additionalMembers]; copy[index] = updated; setAdditionalMembers(copy); }}
                onRemove={() => removeMemberFromGroup(index)}
              />
            ))}
          </div>
        )}
        <div className="border-t border-[var(--border)] pt-4">
          <div className={labelCls}>Add New Member</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <input className={inputCls} placeholder="First Name *" value={newMember.first_name} onChange={e => setNewMember({ ...newMember, first_name: e.target.value })} />
            <input className={inputCls} placeholder="Last Name *" value={newMember.last_name} onChange={e => setNewMember({ ...newMember, last_name: e.target.value })} />
            <input className={inputCls} placeholder="Email (optional)" value={newMember.email} onChange={e => setNewMember({ ...newMember, email: e.target.value })} />
            <input className={inputCls} placeholder="Phone (optional)" value={newMember.phone} onChange={e => setNewMember({ ...newMember, phone: e.target.value })} />
            <input type="date" className={inputCls} value={newMember.date_of_birth} onChange={e => setNewMember({ ...newMember, date_of_birth: e.target.value })} />
            <input className={inputCls} placeholder="Passport Number (optional)" value={newMember.passport_number} onChange={e => setNewMember({ ...newMember, passport_number: e.target.value })} />
          </div>
          <button type="button" onClick={addMemberToGroup} disabled={!newMember.first_name || !newMember.last_name} className={`${primaryBtnCls(!newMember.first_name || !newMember.last_name)} mt-3`}>
            Add Member
          </button>
        </div>
      </section>

      {/* Agent Notes */}
      <section>
        <h3 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-wide mb-4">Agent Notes</h3>
        <textarea name="agent_notes" rows={4} disabled={formLoading} placeholder="Internal notes about the client (not visible to client)" className={`${inputCls} resize-none`} />
      </section>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} disabled={formLoading} className={secondaryBtnCls}>Cancel</button>
        <button type="submit" disabled={formLoading} className={primaryBtnCls(formLoading)}>
          {formLoading ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
        </button>
      </div>
    </form>
  );
};

// ─── Client Detail Panel ────────────────────────────────────────────────────

const ClientDetailPanel = ({ client, onClose, onEdit, onPayment, onLog, onChangeStage, onDelete }) => {
  const [tab, setTab] = useState('profile');
  const [comms, setComms] = useState([]);
  const [commsLoading, setCommsLoading] = useState(false);
  const [commsError, setCommsError] = useState(null);

  useEffect(() => {
    if (tab !== 'comms' || !client) return;
    let cancelled = false;
    setCommsLoading(true);
    setCommsError(null);
    communicationsService.getClientCommunications(client.id)
      .then(data => { if (!cancelled) setComms(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setCommsError('Failed to load communication history.'); })
      .finally(() => { if (!cancelled) setCommsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, client]);

  const tags = parseTags(client);
  const members = parseMembers(client);
  const hasLoyalty = client.hotel_loyalty_number || client.airline_loyalty_number || client.cruise_loyalty_number;
  const hasPassport = client.passport_number || client.passport_expiry;
  const location = [client.city, client.state].filter(Boolean).join(', ');

  const TYPE_ICONS = { email: '📧', phone: '📞', text: '💬', meeting: '🤝', quote_message: '💬', other: '📝' };

  return (
    <div className="flex flex-col h-full bg-white border-l border-[var(--border)] overflow-hidden w-full sm:w-[380px] flex-shrink-0">
      <div className="flex-shrink-0 p-5 border-b border-[var(--border)]">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full bg-[var(--primary)] text-white font-bold text-base flex items-center justify-center flex-shrink-0">{initials(client)}</div>
            <div className="min-w-0">
              <div style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)] leading-tight truncate">{fullName(client)}</div>
              <div className="text-xs text-[var(--muted-foreground)] mt-0.5 truncate">{client.email}</div>
              {location && <div className="text-xs text-[var(--muted-foreground)]">📍 {location}</div>}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex-shrink-0 p-1.5 rounded-lg hover:bg-[var(--muted)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {tags.map(tag => <span key={tag} className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-600 border border-gray-200'}`}>{tag}</span>)}
          </div>
        )}

        {client.workflow_stage_name && (
          <div className="mb-4">
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border"
              style={{ backgroundColor: `${client.workflow_stage_color}20`, color: client.workflow_stage_color, borderColor: client.workflow_stage_color }}
            >
              {client.workflow_stage_name}
            </span>
          </div>
        )}

        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Payment', icon: '💳', action: onPayment, color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
            { label: 'Log', icon: '💬', action: onLog, color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
            { label: 'Stage', icon: '🔀', action: onChangeStage, color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100' },
            { label: 'Edit', icon: '✏️', action: onEdit, color: 'bg-[var(--muted)]/60 text-[var(--foreground)] border-[var(--border)] hover:bg-[var(--muted)]' },
          ].map(btn => (
            <button key={btn.label} onClick={btn.action} className={`flex flex-col items-center gap-1 py-2.5 border rounded-xl text-xs font-semibold transition-colors ${btn.color}`}>
              <span className="text-base">{btn.icon}</span>
              {btn.label}
            </button>
          ))}
        </div>
        <button onClick={onDelete} className="w-full mt-2 py-2 text-xs font-semibold text-red-500 hover:bg-red-50 rounded-lg transition-colors">
          Delete Client
        </button>
      </div>

      <div className="flex border-b border-[var(--border)] flex-shrink-0">
        {['profile', 'comms'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`flex-1 py-3 text-xs font-semibold uppercase tracking-wide transition-colors ${tab === t ? 'text-[var(--accent)] border-b-2 border-[var(--accent)]' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'}`}>
            {t === 'profile' ? 'Profile' : `Comms (${comms.length})`}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {tab === 'profile' ? (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-center">
                <div className="text-2xl font-bold text-[var(--primary)]">{client.quote_count ?? 0}</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Quotes</div>
              </div>
              <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-center">
                <div className="text-2xl font-bold text-[var(--primary)]">{client.itinerary_count ?? 0}</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Itineraries</div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">Contact</div>
              {client.phone && <div className="flex items-center gap-2 text-sm text-[var(--foreground)]"><span>📞</span>{client.phone}</div>}
              {client.address && <div className="flex items-center gap-2 text-sm text-[var(--foreground)]"><span>🏠</span>{client.address}</div>}
              {client.date_of_birth && <div className="flex items-center gap-2 text-sm text-[var(--foreground)]"><span>🎂</span>DOB: {fmtDate(client.date_of_birth)}</div>}
            </div>

            {hasPassport && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">Passport</div>
                <div className="p-3 bg-[var(--muted)]/30 rounded-xl text-sm space-y-1">
                  {client.passport_number && <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Number</span><span className="font-medium">{client.passport_number}</span></div>}
                  {client.passport_expiry && <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Expires</span><span className="font-medium">{fmtDate(client.passport_expiry)}</span></div>}
                  {client.passport_country && <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Country</span><span className="font-medium">{client.passport_country}</span></div>}
                </div>
              </div>
            )}

            {hasLoyalty && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">Loyalty Programs</div>
                <div className="p-3 bg-[var(--muted)]/30 rounded-xl space-y-1.5 text-sm">
                  {client.hotel_loyalty_number && <div className="flex gap-2"><span>🏨</span><span className="text-[var(--foreground)]">{client.hotel_loyalty_number}</span></div>}
                  {client.airline_loyalty_number && <div className="flex gap-2"><span>✈️</span><span className="text-[var(--foreground)]">{client.airline_loyalty_number}</span></div>}
                  {client.cruise_loyalty_number && <div className="flex gap-2"><span>🛳️</span><span className="text-[var(--foreground)]">{client.cruise_loyalty_number}</span></div>}
                </div>
              </div>
            )}

            {client.emergency_contact_name && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">Emergency Contact</div>
                <div className="p-3 bg-[var(--muted)]/30 rounded-xl text-sm">
                  <div className="font-medium text-[var(--foreground)]">{client.emergency_contact_name}</div>
                  {client.emergency_contact_phone && <div className="text-[var(--muted-foreground)]">{client.emergency_contact_phone}</div>}
                  {client.emergency_contact_relation && <div className="text-[var(--muted-foreground)]">{client.emergency_contact_relation}</div>}
                </div>
              </div>
            )}

            {members.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">Group Members ({members.length})</div>
                {members.map((m, i) => (
                  <div key={i} className="flex items-center gap-2.5 p-3 bg-[var(--muted)]/30 rounded-xl">
                    <div className="w-7 h-7 rounded-full bg-[var(--primary)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{m.first_name?.[0]}{m.last_name?.[0]}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[var(--foreground)] truncate">{m.first_name} {m.last_name}</div>
                      {m.email && <div className="text-xs text-[var(--muted-foreground)] truncate">{m.email}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {client.agent_notes && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">Agent Notes</div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-[var(--foreground)] whitespace-pre-wrap">{client.agent_notes}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {commsLoading ? (
              <div className="text-center py-8 text-sm text-[var(--muted-foreground)]">Loading communication history…</div>
            ) : commsError ? (
              <div className="text-center py-8 text-sm text-red-500">{commsError}</div>
            ) : comms.length === 0 ? (
              <div className="text-center py-8 text-sm text-[var(--muted-foreground)]">No communications logged yet.</div>
            ) : (
              comms.map(c => (
                <div key={c.id} className="p-3 bg-[var(--muted)]/30 rounded-xl">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 text-xs font-semibold text-[var(--foreground)]">
                      <span>{TYPE_ICONS[c.communication_type] || '📝'}</span>
                      <span className="capitalize">{c.communication_type}</span>
                      <span className="text-[var(--muted-foreground)] font-normal">· {c.direction}</span>
                    </div>
                    <div className="text-xs text-[var(--muted-foreground)] flex-shrink-0">{fmtDate(c.communication_date)}</div>
                  </div>
                  {c.subject && <div className="text-sm font-medium text-[var(--foreground)]">{c.subject}</div>}
                  {c.content && <div className="text-xs text-[var(--muted-foreground)] mt-1">{c.content}</div>}
                  {!!c.follow_up_required && <div className="text-xs text-amber-600 font-semibold mt-1">Follow-up required{c.follow_up_date ? ` by ${fmtDate(c.follow_up_date)}` : ''}</div>}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main ───────────────────────────────────────────────────────────────────

const ClientsManagement = ({ clients: initialClients, onUpdateClients }) => {
  const { isMobile } = useResponsive();
  const { user } = useAuth();
  // Shared with independent agents via /independent/clients (see App.js) -
  // same clientsService (unified, role-scoped server-side), just a
  // different URL prefix for the one hardcoded cross-page navigation below.
  const basePath = user?.role === 'independent_agent' ? '/independent' : '/agent';

  const [clients, setClients] = useState(initialClients || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Master-detail panel selection (separate from `selectedClient` below,
  // which is specifically the client being moved to a new workflow stage).
  const [detailClient, setDetailClient] = useState(null);

  // Workflow stages state
  const [workflowStages, setWorkflowStages] = useState([]);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Communication logging state
  const [showCommLogger, setShowCommLogger] = useState(false);
  const [commLoggerClient, setCommLoggerClient] = useState(null);

  useEffect(() => {
    setClients(initialClients || []);
  }, [initialClients]);

  useEffect(() => {
    if (!initialClients) {
      fetchClients();
    }
    fetchWorkflowStages();
    // eslint-disable-next-line
  }, []);

  const fetchWorkflowStages = async () => {
    try {
      const stages = await workflowStagesService.getStages();
      setWorkflowStages(stages);
    } catch (err) {
      console.error('Failed to fetch workflow stages:', err);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientsService.getAll();
      setClients(data);
      if (onUpdateClients) onUpdateClients(data);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await clientsService.delete(clientId);
        const updated = clients.filter(c => c.id !== clientId);
        setClients(updated);
        if (onUpdateClients) onUpdateClients(updated);
        setDetailClient(null);
        setSuccess('Client deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error('Error deleting client:', err);
        setError('Failed to delete client. Please try again.');
      }
    }
  };

  const handleRequestPayment = (client) => {
    const paymentRequestUrl = `${basePath}/payment-links?client=${encodeURIComponent(JSON.stringify({
      id: client.id, name: fullName(client), email: client.email, phone: client.phone,
    }))}`;
    window.location.href = paymentRequestUrl;
  };

  const handleSubmitClient = async (formData) => {
    try {
      setFormLoading(true);
      setError(null);

      if (editingClient) {
        await clientsService.update(editingClient.id, formData);
        setSuccess('Client updated successfully');
      } else {
        await clientsService.create(formData);
        setSuccess('Client created successfully');
      }

      await fetchClients();
      setShowModal(false);
      setEditingClient(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving client:', err);
      setError(err.response?.data?.message || 'Failed to save client. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  // CSV Import
  const handleDownloadTemplate = async () => {
    try {
      await clientsService.downloadTemplate();
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template. Please try again.');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      Papa.parse(file, {
        header: true,
        preview: 5,
        complete: (results) => setCsvPreview(results.data),
        error: (err) => {
          console.error('CSV parsing error:', err);
          setError('Failed to parse CSV file. Please check the format.');
        },
      });
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      setError('Please select a CSV file to import.');
      return;
    }
    try {
      setImportLoading(true);
      setError(null);
      const results = await clientsService.bulkImport(csvFile);
      setImportResults(results);
      await fetchClients();
      if (results.summary.successful > 0) {
        setSuccess(`Successfully imported ${results.summary.successful} clients!`);
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error('CSV import error:', err);
      setError(err.response?.data?.error || 'Failed to import CSV. Please try again.');
    } finally {
      setImportLoading(false);
    }
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setCsvFile(null);
    setCsvPreview([]);
    setImportResults(null);
    setError(null);
  };

  const handleMoveClientStage = async (clientId, stageId, reason = null) => {
    try {
      await workflowStagesService.moveClient(clientId, stageId, reason);
      setSuccess('Client moved to new stage successfully');
      await fetchClients();
      setShowStageModal(false);
      setSelectedClient(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to move client:', err);
      setError('Failed to move client to new stage. Please try again.');
    }
  };

  // Filtering + sorting (client-side, matches prior name/email search;
  // tag filter/sort are new, reusing the `tags` column added for this
  // reskin and fields already returned by the clients list endpoint)
  const filtered = clients
    .filter(client => {
      const q = searchTerm.toLowerCase();
      const matchesSearch = q === '' ||
        fullName(client).toLowerCase().includes(q) ||
        (client.email || '').toLowerCase().includes(q) ||
        (client.phone || '').includes(q);
      const matchesTag = filterTag === 'All' || parseTags(client).includes(filterTag);
      return matchesSearch && matchesTag;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return fullName(a).localeCompare(fullName(b));
      if (sortBy === 'trips') return ((b.quote_count || 0) + (b.itinerary_count || 0)) - ((a.quote_count || 0) + (a.itinerary_count || 0));
      if (sortBy === 'added') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return 0;
    });

  const now = new Date();
  const stats = {
    total: clients.length,
    active: clients.filter(c => parseTags(c).includes('Active')).length,
    vip: clients.filter(c => parseTags(c).includes('VIP')).length,
    newThisMonth: clients.filter(c => c.created_at && new Date(c.created_at).getMonth() === now.getMonth() && new Date(c.created_at).getFullYear() === now.getFullYear()).length,
  };

  const FILTER_TABS = [
    { id: 'All', label: `All (${clients.length})` },
    { id: 'Active', label: 'Active' },
    { id: 'VIP', label: '⭐ VIP' },
    { id: 'New', label: 'New' },
    { id: 'Prospect', label: 'Prospect' },
    { id: 'Inactive', label: 'Inactive' },
  ];

  if (loading && !initialClients) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: 'var(--accent)' }} />
      </div>
    );
  }

  return (
    <>
      {/* Add/Edit Client Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-6 px-4" onClick={() => setShowModal(false)}>
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-2xl text-[var(--foreground)]">
                  {editingClient ? `Edit ${fullName(editingClient)}` : 'Add New Client'}
                </h2>
                <p className="text-xs text-[var(--muted-foreground)] mt-0.5">
                  {editingClient ? 'Update client information and travel profile.' : 'Fill in the lead traveler details to create a new client record.'}
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] p-1.5 rounded-lg hover:bg-[var(--muted)]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(100vh-160px)] px-6 py-6">
              <ClientFormComponent
                isEdit={!!editingClient}
                initialData={editingClient}
                onSubmit={handleSubmitClient}
                onCancel={() => setShowModal(false)}
                formLoading={formLoading}
              />
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-6 px-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
              <h3 style={{ fontFamily: 'var(--font-display)' }} className="text-2xl text-[var(--foreground)]">Import Clients from CSV</h3>
              <button onClick={resetImportModal} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] p-1.5 rounded-lg hover:bg-[var(--muted)]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(100vh-160px)]">
              {!importResults ? (
                <div className="space-y-5">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">Supported CSV Formats</h4>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p>This importer supports two CSV formats:</p>
                      <ul className="list-disc list-inside space-y-0.5 ml-2">
                        <li><strong>TPCRM Format:</strong> first_name, last_name, email, phone (+ optional fields)</li>
                        <li><strong>WordPress Legacy:</strong> member_1_name, email, phone, member_2_name, etc.</li>
                      </ul>
                      <p className="mt-2">The system will automatically detect which format you're using. Required fields: First Name, Last Name, Email, Phone.</p>
                    </div>
                    <button onClick={handleDownloadTemplate} className="mt-3 px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-xs font-semibold hover:bg-[var(--primary)]/90 transition-colors">
                      Download Template
                    </button>
                  </div>

                  <div>
                    <label className={labelCls}>Select CSV File</label>
                    <input type="file" accept=".csv" onChange={handleFileSelect} className="block w-full text-sm text-[var(--muted-foreground)]" />
                  </div>

                  {csvPreview.length > 0 && (
                    <div>
                      <div className={labelCls}>Preview (First 5 rows)</div>
                      <div className="overflow-x-auto border border-[var(--border)] rounded-xl">
                        <table className="min-w-full text-sm">
                          <thead className="bg-[var(--muted)]/40">
                            <tr>
                              {Object.keys(csvPreview[0] || {}).map(header => (
                                <th key={header} className="px-3 py-2 text-left text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">{header}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[var(--border)]">
                            {csvPreview.map((row, i) => (
                              <tr key={i}>
                                {Object.values(row).map((value, j) => (
                                  <td key={j} className="px-3 py-2 whitespace-nowrap text-[var(--foreground)]">{value}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-2">
                    <button onClick={resetImportModal} className={secondaryBtnCls}>Cancel</button>
                    <button onClick={handleImportCSV} disabled={!csvFile || importLoading} className="px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-40">
                      {importLoading ? 'Importing...' : 'Import Clients'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-green-800">Import Complete</h3>
                    <div className="text-sm text-green-700 mt-2 space-y-1">
                      <p>Successfully imported {importResults.summary.successful} clients</p>
                      {importResults.results?.length > 0 && (
                        <p>Total family members imported: {importResults.results.reduce((t, r) => t + (r.additionalMembersCount || 0), 0)}</p>
                      )}
                      {importResults.summary.failed > 0 && <p>Failed to import {importResults.summary.failed} clients</p>}
                    </div>
                  </div>
                  {importResults.errors?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-red-700 mb-2">Import Errors:</h4>
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 max-h-40 overflow-y-auto space-y-1">
                        {importResults.errors.map((err, i) => (
                          <div key={i} className="text-xs text-red-700">Row {err.row}: {err.error}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <button onClick={resetImportModal} className={primaryBtnCls(false)}>Done</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Move Stage Modal */}
      {showStageModal && selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 py-5 border-b border-[var(--border)]">
              <h3 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Move Client to New Stage</h3>
              <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{fullName(selectedClient)}</p>
            </div>
            <div className="px-6 py-6">
              <div className="mb-5">
                <label className={labelCls}>Current Stage</label>
                <div
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium border"
                  style={{ backgroundColor: `${selectedClient.workflow_stage_color}20`, color: selectedClient.workflow_stage_color, borderColor: selectedClient.workflow_stage_color }}
                >
                  {selectedClient.workflow_stage_name || 'No Stage'}
                </div>
              </div>
              <div className="mb-5">
                <label className={labelCls}>New Stage</label>
                <select id="newStage" className={`${inputCls} cursor-pointer`} defaultValue="">
                  <option value="">Select a stage...</option>
                  {workflowStages.filter(s => s.id !== selectedClient.workflow_stage_id).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-6">
                <label className={labelCls}>Reason (Optional)</label>
                <textarea id="moveReason" rows={3} className={`${inputCls} resize-none`} placeholder="Why are you moving this client to a new stage?" />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowStageModal(false); setSelectedClient(null); }} className={secondaryBtnCls}>Cancel</button>
                <button
                  onClick={() => {
                    const newStageId = document.getElementById('newStage').value;
                    const reason = document.getElementById('moveReason').value;
                    if (newStageId) handleMoveClientStage(selectedClient.id, parseInt(newStageId, 10), reason || null);
                  }}
                  className={primaryBtnCls(false)}
                >
                  Move Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Communication Logger Modal */}
      {showCommLogger && commLoggerClient && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-6 px-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <CommunicationLogger
              clientId={commLoggerClient.id}
              onClose={() => { setShowCommLogger(false); setCommLoggerClient(null); }}
              onSaved={() => {
                setShowCommLogger(false);
                setCommLoggerClient(null);
                setSuccess('Communication logged successfully');
                setTimeout(() => setSuccess(null), 3000);
              }}
            />
          </div>
        </div>
      )}

      <div className={`flex h-full min-h-0 ${detailClient ? 'overflow-hidden' : ''}`}>
        {/* Main content */}
        <div className="flex-1 overflow-y-auto min-w-0">
          <div className="max-w-4xl mx-auto space-y-6 pb-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Client Management</h1>
                <p className="text-[var(--muted-foreground)] text-sm">Manage your client database, track trips, and log communications.</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setShowImportModal(true)} className="hidden sm:flex items-center gap-2 px-4 py-2.5 border border-[var(--border)] bg-white rounded-xl text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                  Import
                </button>
                <button onClick={handleCreateClient} className="flex items-center gap-2 px-4 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all shadow-sm">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  Add Client
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3">{success}</div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Total Clients', value: stats.total, icon: '👥', color: 'text-[var(--primary)]' },
                { label: 'Active', value: stats.active, icon: '✈️', color: 'text-green-600' },
                { label: 'VIP Clients', value: stats.vip, icon: '⭐', color: 'text-amber-600' },
                { label: 'Added This Month', value: stats.newThisMonth, icon: '🆕', color: 'text-blue-600' },
              ].map(s => (
                <div key={s.label} className="bg-white border border-[var(--border)] rounded-xl p-4">
                  <div className="text-xl mb-1">{s.icon}</div>
                  <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Search + sort */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                <input
                  type="text"
                  placeholder="Search by name, email, or phone…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors"
                />
              </div>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors cursor-pointer flex-shrink-0">
                <option value="name">Sort: Name A–Z</option>
                <option value="trips">Sort: Most Activity</option>
                <option value="added">Sort: Recently Added</option>
              </select>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {FILTER_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setFilterTag(tab.id)}
                  className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${filterTag === tab.id ? 'bg-[var(--primary)] text-white' : 'bg-white border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)]'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Client list */}
            {filtered.length === 0 ? (
              <div className="text-center py-16 bg-white border-2 border-dashed border-[var(--border)] rounded-2xl">
                <div className="text-4xl mb-3">👥</div>
                <div className="text-sm font-medium text-[var(--foreground)] mb-1">{searchTerm ? 'No clients match your search' : 'No clients yet'}</div>
                <div className="text-xs text-[var(--muted-foreground)] mb-4">{searchTerm ? 'Try a different name, email, or phone number' : 'Add your first client to get started'}</div>
                {!searchTerm && <button onClick={handleCreateClient} className={primaryBtnCls(false)}>Add First Client</button>}
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map(client => {
                  const isSelected = detailClient?.id === client.id;
                  const tags = parseTags(client);
                  return (
                    <div
                      key={client.id}
                      onClick={() => setDetailClient(isSelected ? null : client)}
                      className={`bg-white border-2 rounded-xl transition-all cursor-pointer hover:shadow-sm ${isSelected ? 'border-[var(--accent)] shadow-sm' : 'border-[var(--border)] hover:border-[var(--accent)]/40'}`}
                    >
                      <div className="flex items-center gap-4 px-4 py-3.5">
                        <div className="w-11 h-11 rounded-full bg-[var(--muted)] text-[var(--foreground)] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {initials(client)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[var(--foreground)] text-sm truncate">{fullName(client)}</span>
                            {tags.slice(0, 2).map(tag => (
                              <span key={tag} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-500'}`}>{tag}</span>
                            ))}
                          </div>
                          <div className="text-xs text-[var(--muted-foreground)] mt-0.5 truncate">{client.email}{client.phone ? ` · ${client.phone}` : ''}</div>
                        </div>
                        <div className="text-right flex-shrink-0 hidden sm:block">
                          <div className="text-xs font-medium text-[var(--muted-foreground)]">{client.quote_count || 0} quote{client.quote_count !== 1 ? 's' : ''}</div>
                          {client.workflow_stage_name && (
                            <div className="text-xs mt-0.5" style={{ color: client.workflow_stage_color }}>{client.workflow_stage_name}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <ClientRegistrationLink />
          </div>
        </div>

        {/* Detail panel */}
        {detailClient && (
          <ClientDetailPanel
            client={detailClient}
            onClose={() => setDetailClient(null)}
            onEdit={() => handleEditClient(detailClient)}
            onPayment={() => handleRequestPayment(detailClient)}
            onLog={() => { setCommLoggerClient(detailClient); setShowCommLogger(true); }}
            onChangeStage={() => { setSelectedClient(detailClient); setShowStageModal(true); }}
            onDelete={() => handleDeleteClient(detailClient.id)}
          />
        )}
      </div>
    </>
  );
};

export default ClientsManagement;
